import { authClient } from "@/lib/auth-client"
import {
	Avatar,
	AvatarFallback,
	AvatarImage,
} from "@preview-kit/ui/components/avatar"
import { Button } from "@preview-kit/ui/components/button"
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@preview-kit/ui/components/popover"
import { Separator } from "@preview-kit/ui/components/separator"
import { Textarea } from "@preview-kit/ui/components/textarea"
import { cn } from "@preview-kit/ui/lib/utils"
import { SendHorizontal } from "lucide-react"
import { useEffect, useRef } from "react"
import "./index.css"

interface Position {
	x: number
	y: number
}

interface CommentInputPopoverProps {
	commentInput: {
		content: string
		isVisible: boolean
	} | null
	computedPosition: Position | null
	onClose: () => void
	onContentChange: (content: string) => void
	onSave: () => void
}

export function CommentInputPopover({
	commentInput,
	computedPosition,
	onClose,
	onContentChange,
	onSave,
}: CommentInputPopoverProps) {
	const session = authClient.useSession()
	const textareaRef = useRef<HTMLTextAreaElement>(null)

	// Manually handle focus since autoFocus doesn't work reliably in Shadow DOM
	useEffect(() => {
		if (commentInput?.isVisible && textareaRef.current) {
			// Use setTimeout to ensure the portal is fully rendered
			setTimeout(() => {
				textareaRef.current?.focus()
			}, 0)
		}
	}, [commentInput?.isVisible])

	if (!commentInput || !computedPosition) {
		return null
	}

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault()
			onSave()
		}
	}

	return (
		<Popover open={true} onOpenChange={onClose}>
			<PopoverTrigger asChild>
				<div
					className="comment-input-container absolute z-300 cursor-pointer"
					style={{
						left: `${computedPosition.x}px`,
						top: `${computedPosition.y}px`,
						transform: "translate(0%, -100%)",
					}}
				>
					<div className=" p-1 rounded-t-3xl rounded-br-3xl bg-[#ee0b4b] flex items-center justify-center">
						<Avatar className="w-7 h-7">
							<AvatarImage src={session.data?.user.image || ""} />
							<AvatarFallback>CN</AvatarFallback>
						</Avatar>
					</div>
				</div>
			</PopoverTrigger>
			<PopoverContent
				container={window.__PREVIEWKIT_PORTAL_CONTAINER__}
				className={cn(
					"popover_comment text-black",
					"w-90 p-2 bg-white/90 shadow-xl z-300",
					"border border-neutral-300",
				)}
				side="right"
				align="start"
				sideOffset={10}
			>
				<Textarea
					ref={textareaRef}
					value={commentInput.content}
					onChange={(e) => onContentChange(e.target.value)}
					onKeyDown={handleKeyDown}
					placeholder="Type your comment..."
					className="field-sizing-content shadow-none min-h-0 resize-none p-1 bg-transparent border-none focus-visible:border-none focus-visible:ring-0 focus-visible:outline-none"
					aria-label="Comment text"
					rows={1}
				/>
				<Separator className="my-2 h-[1px] !bg-neutral-300" />
				<div className="flex items-center">
					{/* <Image className="w-5 h-5 text-gray-400 cursor-pointer" /> */}
					<Button
						size="icon"
						className="rounded-full w-8 h-8 ml-auto"
						onClick={onSave}
						disabled={!commentInput.content.trim()}
						aria-label="Save comment"
						title="Save comment"
					>
						<SendHorizontal />
					</Button>
				</div>
			</PopoverContent>
		</Popover>
	)
}
